import { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Search, Users, Pencil, Ban, Trash2, RefreshCcw, UserPlus, Undo } from "lucide-react";
import { Modal, Field, FieldSelect, FieldTextarea } from "./AdminBusinesses";

const TYPES = ["", "business_owner", "employee", "customer", "promoter", "platform_admin"];

export default function AdminUsers() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [type, setType] = useState("");
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (type) params.set("account_type", type);
    const { data } = await api.get(`/admin/users?${params}`);
    setItems(data.items || []);
  }, [q, type]);

  useEffect(() => { load(); }, [load]);

  const ban = async (u) => {
    if (!window.confirm(`Ban ${u.email}? This deactivates the user and blocks future sign-ups with this email.`)) return;
    await api.post(`/admin/users/${u.id}/ban`);
    toast.success("User banned.");
    load();
  };
  const unban = async (u) => {
    await api.post(`/admin/users/${u.id}/unban`);
    toast.success("User unbanned.");
    load();
  };
  const doDelete = async () => {
    await api.delete(`/admin/users/${confirm.id}`);
    toast.success("User deleted.");
    setConfirm(null);
    load();
  };
  const saveCreate = async (form) => {
    try {
      await api.post(`/admin/users`, form);
      toast.success("User created.");
      setCreating(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed."); }
  };
  const saveEdit = async (patch) => {
    try {
      await api.put(`/admin/users/${editing.id}`, patch);
      toast.success("User updated.");
      setEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed."); }
  };

  return (
    <div data-testid="admin-users-page">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="font-display text-2xl font-semibold flex items-center gap-2">
          <Users className="w-5 h-5" /> Users
        </h1>
        <button data-testid="admin-users-create" onClick={() => setCreating(true)}
                className="px-3 py-1.5 text-sm rounded-lg bg-[#1B4332] text-white inline-flex items-center gap-1">
          <UserPlus className="w-3.5 h-3.5" /> Create user
        </button>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
          <input data-testid="admin-users-search" value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Email or name..."
                 className="pl-7 pr-3 py-1.5 text-sm rounded-lg border border-slate-300" />
        </div>
        <select data-testid="admin-users-type-filter" value={type} onChange={(e) => setType(e.target.value)}
                className="px-2 py-1.5 text-sm rounded-lg border border-slate-300">
          {TYPES.map((t) => <option key={t} value={t}>{t || "Any type"}</option>)}
        </select>
        <button onClick={load} className="px-3 py-1.5 text-sm rounded-lg border border-slate-300 hover:bg-slate-50 inline-flex items-center gap-1">
          <RefreshCcw className="w-3.5 h-3.5" /> Refresh
        </button>
        <span className="text-xs text-slate-500 ml-auto">{items.length} users</span>
      </div>
      <div className="mt-4 overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="text-left p-2">Email</th>
              <th className="text-left p-2">Name</th>
              <th className="text-left p-2">Type</th>
              <th className="text-left p-2">Active</th>
              <th className="text-right p-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-t border-slate-100">
                <td className="p-2 font-medium">{u.email}</td>
                <td className="p-2">{u.name}</td>
                <td className="p-2 text-xs"><span className="px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200">{u.account_type}</span></td>
                <td className="p-2">{u.active ? <span className="text-emerald-700">Yes</span> : <span className="text-rose-700">No</span>}</td>
                <td className="p-2 text-right whitespace-nowrap">
                  <button data-testid={`user-edit-${u.id}`} onClick={() => setEditing(u)}
                          className="text-xs px-2 py-1 rounded border border-slate-300 inline-flex items-center gap-1">
                    <Pencil className="w-3 h-3" /> Edit
                  </button>
                  {u.active ? (
                    <button data-testid={`user-ban-${u.id}`} onClick={() => ban(u)}
                            className="ml-1 text-xs px-2 py-1 rounded border border-amber-300 text-amber-800 inline-flex items-center gap-1">
                      <Ban className="w-3 h-3" /> Ban
                    </button>
                  ) : (
                    <button data-testid={`user-unban-${u.id}`} onClick={() => unban(u)}
                            className="ml-1 text-xs px-2 py-1 rounded border border-emerald-300 text-emerald-700 inline-flex items-center gap-1">
                      <Undo className="w-3 h-3" /> Unban
                    </button>
                  )}
                  <button data-testid={`user-delete-${u.id}`} onClick={() => setConfirm(u)}
                          className="ml-1 text-xs px-2 py-1 rounded border border-rose-300 text-rose-700 inline-flex items-center gap-1">
                    <Trash2 className="w-3 h-3" /> Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && <UserEditModal item={editing} onClose={() => setEditing(null)} onSave={saveEdit} />}
      {creating && <UserCreateModal onClose={() => setCreating(false)} onSave={saveCreate} />}
      {confirm && (
        <Modal title={`Delete ${confirm.email}?`} onClose={() => setConfirm(null)} testId="confirm-delete-user">
          <p className="text-sm text-slate-700">This permanently deletes the user record. The action cannot be undone.</p>
          <div className="mt-4 flex justify-end gap-2">
            <button onClick={() => setConfirm(null)} className="px-3 py-1.5 text-sm rounded border border-slate-300">Cancel</button>
            <button data-testid="confirm-delete-user-yes" onClick={doDelete}
                    className="px-3 py-1.5 text-sm rounded bg-rose-600 text-white">Yes, delete</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function UserCreateModal({ onClose, onSave }) {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", password: "", account_type: "customer", notes: "",
  });
  return (
    <Modal title="Create user" onClose={onClose} testId="user-create-modal">
      <div className="space-y-3 text-sm">
        <Field label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
        <Field label="Email" value={form.email} type="email" onChange={(v) => setForm({ ...form, email: v })} />
        <Field label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
        <Field label="Password" value={form.password} type="password" onChange={(v) => setForm({ ...form, password: v })} />
        <FieldSelect label="Account type" value={form.account_type}
                       options={["customer", "business_owner", "employee", "promoter", "platform_admin"]}
                       onChange={(v) => setForm({ ...form, account_type: v })} />
        <FieldTextarea label="Notes" value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} />
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <button onClick={onClose} className="px-3 py-1.5 text-sm rounded border border-slate-300">Cancel</button>
        <button data-testid="user-create-save" onClick={() => onSave(form)} className="px-3 py-1.5 text-sm rounded bg-[#1B4332] text-white">Create</button>
      </div>
    </Modal>
  );
}

function UserEditModal({ item, onClose, onSave }) {
  const [form, setForm] = useState({
    name: item.name || "", phone: item.phone || "",
    account_type: item.account_type || "customer",
    active: !!item.active, notes: item.notes || "", new_password: "",
  });
  return (
    <Modal title={`Edit ${item.email}`} onClose={onClose} testId="user-edit-modal">
      <div className="space-y-3 text-sm">
        <Field label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
        <Field label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
        <FieldSelect label="Account type" value={form.account_type}
                       options={["customer", "business_owner", "employee", "promoter", "platform_admin"]}
                       onChange={(v) => setForm({ ...form, account_type: v })} />
        <label className="flex items-center gap-2">
          <input type="checkbox" data-testid="user-edit-active" checked={form.active}
                 onChange={(e) => setForm({ ...form, active: e.target.checked })} />
          <span className="text-xs">Active</span>
        </label>
        <Field label="New password (optional)" type="password" value={form.new_password}
                onChange={(v) => setForm({ ...form, new_password: v })} />
        <FieldTextarea label="Notes" value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} />
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <button onClick={onClose} className="px-3 py-1.5 text-sm rounded border border-slate-300">Cancel</button>
        <button data-testid="user-edit-save" onClick={() => {
          const patch = { ...form };
          if (!patch.new_password) delete patch.new_password;
          onSave(patch);
        }} className="px-3 py-1.5 text-sm rounded bg-[#1B4332] text-white">Save</button>
      </div>
    </Modal>
  );
}
